/**
 * Переходы режима «Свой текст».
 *
 * Текст пользователя живёт в состоянии экрана и в локальном хранилище
 * прогресса — и больше нигде. На сервер уходит только итог сессии без единиц:
 * идентификатор единицы этого режима сам по себе есть кусок текста
 * (ТЗ §11.2, решение AD-004).
 */

import type { MasteryStat, SessionId, UnitOutcome } from '@type2x/shared-contracts';
import type { TypingSession } from '@type2x/typing-engine';
import { DEFAULT_LEARNING_CONFIG } from '@type2x/learning-engine';
import type { Workspace } from '@type2x/feature-personal-workspace';
import {
  activeProfile,
  appendXp,
  recordSession,
  upsertMastery,
} from '@type2x/feature-personal-workspace';
import {
  applyCustomOutcomes,
  buildCustomTextPlan,
  collectCustomOutcomes,
  parseCustomText,
  type CustomTextPlan,
  type CustomTextSnapshot,
} from '@type2x/feature-custom-text';
import { buildSessionSummary } from '@type2x/feature-progress';
import { buildSessionPayload, type SessionPayload } from '@type2x/feature-sync';

import type { Runtime } from '../runtime.js';
import type { AppScreen } from './app-state.js';
import { countXpToday, resultScreen, type Transition } from './flow.js';

const CONFIG = DEFAULT_LEARNING_CONFIG;

/** Снимок навыка для режима «Свой текст». */
export function customTextSnapshot(workspace: Workspace): CustomTextSnapshot {
  const profile = activeProfile(workspace);

  return {
    language: profile.language,
    layoutId: profile.keyboard.layoutId,
    platform: profile.keyboard.platform,
    units: profile.units,
    stats: profile.stats,
  };
}

/** Пустой экран режима: пользователь ещё не принёс текст. */
export function customTextScreen(runtime: Runtime): AppScreen {
  return {
    name: 'custom-text',
    plan: null,
    tooShort: false,
    sessionId: runtime.ids.newSessionId(),
  };
}

/**
 * Разбирает принесённый текст.
 *
 * Материала может не хватить: из трёх слов упражнение выродится в повтор.
 * Тогда экран остаётся формой и говорит об этом, а не строит бессмысленное
 * занятие.
 */
export function planFromText(runtime: Runtime, workspace: Workspace, text: string): AppScreen {
  const snapshot = customTextSnapshot(workspace);
  const parsed = parseCustomText(text, snapshot);

  if (!parsed.usable) {
    return {
      name: 'custom-text',
      plan: null,
      tooShort: true,
      sessionId: runtime.ids.newSessionId(),
    };
  }

  const plan = buildCustomTextPlan(
    snapshot,
    parsed,
    `${runtime.installationId}:${runtime.clock.epochMs()}`,
    runtime.clock.epochMs(),
    CONFIG,
  );

  return { name: 'custom-text', plan, tooShort: false, sessionId: runtime.ids.newSessionId() };
}

/** Завершает занятие: обновляет слова, начисляет XP и собирает итог. */
export function finishCustomText(
  runtime: Runtime,
  workspace: Workspace,
  plan: CustomTextPlan,
  session: TypingSession,
  sessionId: SessionId,
): Transition {
  const now = runtime.clock.isoNow();
  const profile = activeProfile(workspace);

  const outcomes = collectCustomOutcomes(
    plan.exercise,
    session.keystrokeEvents,
    session.typingErrors,
  );
  const statsAfter = applyCustomOutcomes(plan.units, profile.stats, outcomes, CONFIG, now);

  // Слова пользователя становятся единицами локального прогресса: они нужны
  // режиму «Мои ошибки» и повторению, но наружу не уходят.
  let next = upsertMastery(workspace, plan.units, statsAfter, now);
  next = recordSession(next, session.metrics, now);

  const summary = buildSessionSummary({
    metrics: session.metrics,
    baseline: profile.baseline,
    units: activeProfile(next).units,
    statsBefore: profile.stats,
    statsAfter,
    previousXp: next.xpLedger.reduce((sum, item) => sum + item.amount, 0),
    xpOccurrencesToday: countXpToday(next, now),
    activeDays: activeProfile(next).practiceDays.length,
    retained: false,
    isControlled: false,
    awardedAt: now,
    config: CONFIG,
  });

  for (const transaction of summary.xpAwarded) {
    next = appendXp(next, transaction, now);
  }

  return {
    workspace: next,
    screen: resultScreen(next, summary, { kind: 'custom-text' }, now),
    sessionPayload: payloadFor(runtime, workspace, sessionId, session, outcomes, statsAfter),
  };
}

/**
 * Итог сессии для отправки.
 *
 * Единицы этого режима отбрасывает слой синхронизации: приставка `custom:`
 * помечает материал, который остаётся на устройстве. Здесь они передаются как
 * есть, чтобы фильтр был один и его нельзя было обойти по невнимательности.
 */
function payloadFor(
  runtime: Runtime,
  workspace: Workspace,
  sessionId: SessionId,
  session: TypingSession,
  outcomes: readonly UnitOutcome[],
  statsAfter: readonly MasteryStat[],
): SessionPayload {
  const profile = activeProfile(workspace);

  return buildSessionPayload({
    syncId: runtime.ids.newSyncId(),
    sessionId,
    mode: 'custom-text',
    language: profile.language,
    layoutId: profile.keyboard.layoutId,
    startedAt: new Date(runtime.clock.epochMs() - session.metrics.elapsedMs).toISOString(),
    durationMs: session.metrics.elapsedMs,
    metrics: session.metrics,
    outcomes,
    statsAfter,
    errors: session.typingErrors,
    isControlled: false,
  });
}
